import dayjs from 'dayjs';

import type {
	MaterialMovementRow,
	MovementReportBar,
	MovementReportLot,
} from '../../../../redux/api/material-movements';
import {
	type BarSummary,
	type LocationFormatter,
	asIs,
	formatSpan,
	netLocationEnds,
	SHOP_TIMEZONE,
	summarizeBar,
} from '../../../../components/MaterialMovements/movementMeta';

export interface AuditBar {
	key: string;
	bar: MovementReportBar;
	summary: BarSummary;
	familyRoot: string | null;
}

/** One printed / on-screen line: a bar, or identical bars merged together. */
export interface AuditLine {
	key: string;
	bars: AuditBar[];
	barIds: string;
	timeSpan: string;
	activity: string;
	length: string;
	location: string;
	/** Set only when the bar is no longer where the range left it. */
	nowAt: string | null;
	operators: string;
	operatorNames: string[];
	notes: string;
	historical: boolean;
}

export interface AuditSection {
	key: string;
	materialName: string;
	barCount: number;
	lines: AuditLine[];
}

export interface AuditLineOptions {
	showDate: boolean;
	formatLocation: LocationFormatter;
}

const unique = (values: (string | null | undefined)[]) =>
	Array.from(new Set(values.filter((v): v is string => Boolean(v))));

const joinParts = (parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' · ');

/** "Brian Becker" → "B. Becker"; single names and "Unknown" stay as they are. */
export const shortOperatorName = (name: string) => {
	const parts = name.trim().split(/\s+/);
	// Station logins such as "Machine 01 Operator" are not people's names; keep them whole.
	if (parts.length < 2 || /\d/.test(name)) return name.trim();
	return `${parts[0][0]}. ${parts.slice(1).join(' ')}`;
};

export const formatOperators = (names: string[]) => {
	if (names.length === 0) return '—';
	const first = shortOperatorName(names[0]);
	return names.length > 1 ? `${first} +${names.length - 1}` : first;
};

const nowAtFor = (bar: MovementReportBar, format: LocationFormatter) => {
	const rows = bar.movements;
	const last = rows[rows.length - 1];
	if (bar.deleted) return last?.action === 'deleted' ? null : 'deleted';
	const { end } = netLocationEnds(rows);
	if (bar.current_location === end) return null;
	return bar.current_location ? `now ${format(bar.current_location)}` : 'now no location';
};

const businessDay = (row: MaterialMovementRow | undefined) =>
	row ? dayjs(row.occurred_at).tz(SHOP_TIMEZONE).format('YYYY-MM-DD') : '';

/**
 * Bars merge only when an auditor would tick them the same way: same steps,
 * same start and end location, same length, same business day.
 */
const mergeKey = (item: AuditBar, nowAt: string | null) => {
	const rows = item.bar.movements;
	const { start, end } = netLocationEnds(rows);
	return [
		rows.map((r) => r.action).join('>'),
		start ?? '',
		end ?? '',
		item.summary.netLength,
		businessDay(rows[rows.length - 1]),
		nowAt ?? '',
	].join('|');
};

const toAuditBars = (lot: MovementReportLot, options: AuditLineOptions): AuditBar[] =>
	lot.families.flatMap((family) =>
		family.bars.map((bar) => ({
			key: bar.id ?? bar.friendly_id,
			bar,
			summary: summarizeBar(bar, options),
			familyRoot:
				family.bars.length > 1 && bar.friendly_id !== family.root_friendly_id
					? family.root_friendly_id
					: null,
		}))
	);

const buildLine = (bars: AuditBar[], nowAt: string | null, showDate: boolean): AuditLine => {
	const [first] = bars;
	const rows = bars
		.flatMap((b) => b.bar.movements)
		.sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));
	const ids = bars.map((b) => b.bar.friendly_id);
	const operatorNames = unique(bars.flatMap((b) => b.summary.operators));
	const parents = unique(
		bars.flatMap((b) =>
			b.summary.parentBars.length ? b.summary.parentBars : b.familyRoot ? [b.familyRoot] : []
		)
	);
	const singleNote =
		bars.length === 1 && first.summary.count === 1 ? first.bar.movements[0].notes : null;
	return {
		key: bars.map((b) => b.key).join('+'),
		bars,
		barIds: ids.length > 1 ? `${ids.join(', ')} (${ids.length})` : ids[0],
		timeSpan: rows.length
			? formatSpan(rows[0].occurred_at, rows[rows.length - 1].occurred_at, showDate)
			: '—',
		activity: first.summary.actionChain,
		length: first.summary.netLength,
		location: first.summary.netLocation,
		nowAt,
		operators: formatOperators(operatorNames),
		operatorNames,
		notes: joinParts([
			...unique(bars.flatMap((b) => b.summary.jobs)),
			parents.length > 0 && `from ${parents.join(', ')}`,
			singleNote,
		]),
		historical: bars.some((b) => b.summary.hasBackfilled),
	};
};

/** Material sections of compact audit lines, shared by the web table and the printout. */
export const buildAuditLines = (
	groups: MovementReportLot[],
	options: AuditLineOptions
): AuditSection[] => {
	// Each Material row is a received lot, so one spec can arrive as several groups with the same name.
	const byName = new Map<string, { key: string; bars: AuditBar[] }>();
	groups.forEach((lot) => {
		const entry = byName.get(lot.material_name);
		const bars = toAuditBars(lot, options);
		if (entry) entry.bars.push(...bars);
		else byName.set(lot.material_name, { key: lot.material_id ?? lot.material_name, bars });
	});
	return Array.from(byName, ([materialName, { key: sectionKey, bars }]) => {
		const merged = new Map<string, { bars: AuditBar[]; nowAt: string | null }>();
		bars.forEach((item) => {
			const nowAt = nowAtFor(item.bar, options.formatLocation);
			// Keyed on the saved location: two places can shorten alike.
			const key = item.bar.movements.length
				? mergeKey(item, nowAtFor(item.bar, asIs))
				: `bar:${item.key}`;
			const entry = merged.get(key);
			if (entry) entry.bars.push(item);
			else merged.set(key, { bars: [item], nowAt });
		});
		return {
			key: sectionKey,
			materialName,
			barCount: bars.length,
			lines: Array.from(merged.values()).map(({ bars: lineBars, nowAt }) =>
				buildLine(lineBars, nowAt, options.showDate)
			),
		};
	});
};
